import { makeStyles, Typography } from '@paintscout/ui'
import type { MuiTheme as Theme } from '@paintscout/ui'
import CheckIcon from '@material-ui/icons/Check'
import classnames from 'classnames'
import React from 'react'
import { IMPORT_STEPS } from './useImportWizard'

const useStyles = makeStyles<Theme>((theme) => ({
  root: {
    display: 'flex',
    gap: theme.spacing(2),
    padding: theme.spacing(0, 0, 3)
  },
  segment: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1)
  },
  bar: {
    height: 4,
    borderRadius: 2,
    backgroundColor: theme.paintscout.palette.brand.Neutral.P85
  },
  barActive: {
    backgroundColor: theme.palette.primary.main
  },
  barDone: {
    backgroundColor: theme.palette.primary.main
  },
  label: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1)
  },
  // Pending and active markers are outlined rings on white; only a completed step is filled solid
  // blue. Matches the mock: grey ring + grey number ahead of you, blue ring + blue number for where
  // you are, solid blue + white check behind you.
  marker: {
    width: 22,
    height: 22,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    fontSize: 12,
    fontWeight: 600,
    lineHeight: 1,
    backgroundColor: theme.paintscout.palette.brand.Neutral.White,
    border: `1.5px solid ${theme.paintscout.palette.brand.Neutral.P85}`,
    color: theme.paintscout.palette.brand.Neutral.P70
  },
  markerActive: {
    backgroundColor: theme.paintscout.palette.secondary[260].C95,
    borderColor: theme.palette.primary.main,
    color: theme.palette.primary.main
  },
  markerDone: {
    backgroundColor: theme.palette.primary.main,
    borderColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText
  },
  checkIcon: {
    fontSize: 14
  },
  // Steps still ahead are greyed out; the one you are on and the ones behind you read as dark. No
  // step is bolded — the marker carries the state. `em`, not `rem`, so the size still follows the
  // theme's fontScale (body2 is deliberately the same size as body1 in this theme).
  labelText: {
    fontSize: '0.875em',
    color: theme.paintscout.palette.brand.Neutral.P70
  },
  labelTextDone: {
    color: theme.palette.text.primary
  },
  labelTextActive: {
    color: theme.palette.text.primary
  }
}))

export interface ImportStepperProps {
  /** Index of the step the user is on, 0-3. */
  activeStep: number
}

/**
 * The four-segment stepper shown on every wizard screen: a coloured bar over a numbered circle
 * that becomes a check once the step is behind the user.
 */
export default function ImportStepper({ activeStep }: ImportStepperProps) {
  const classes = useStyles()

  return (
    <div className={classes.root} data-testid="import-stepper">
      {IMPORT_STEPS.map((label, index) => {
        const isDone = index < activeStep
        const isActive = index === activeStep
        const state = isDone ? 'done' : isActive ? 'active' : 'pending'

        return (
          <div key={label} className={classes.segment} data-testid={`import-step-${index}`} data-state={state}>
            <div
              className={classnames(classes.bar, {
                [classes.barDone]: isDone,
                [classes.barActive]: isActive
              })}
            />
            <div className={classes.label}>
              <span
                className={classnames(classes.marker, {
                  [classes.markerDone]: isDone,
                  [classes.markerActive]: isActive
                })}
              >
                {isDone ? <CheckIcon className={classes.checkIcon} aria-label="complete" /> : index + 1}
              </span>
              <Typography
                variant="body2"
                className={classnames(classes.labelText, {
                  [classes.labelTextDone]: isDone,
                  [classes.labelTextActive]: isActive
                })}
              >
                {label}
              </Typography>
            </div>
          </div>
        )
      })}
    </div>
  )
}
